import { useEffect, useMemo } from 'react'
import { useLocationHours, useStartDateAvailability } from '@/api/enrollment-api'
import { useEnrollmentStore } from '@/stores'
import {
  DEFAULT_TRAINING_HOURS,
  formatHourRange,
  getAllowedTrainingDays,
  getClassroomScheduleDisplay,
  isTrainingStartDateAllowed,
  getTrainingProgramMode
} from './helpers'

type UseTrainingDateStepArgs = {
  locationExternalId?: string | null
  isTrainingStartDateDisabled: boolean
  onTrainingStartDateChange: (value: string) => void
  onYardDaysChange: (days: string[]) => void
  trainingStartDate: string
  trainingMode: string | null | undefined
  yardDays: string[]
}

export function useTrainingDateStep({
  locationExternalId,
  isTrainingStartDateDisabled,
  onTrainingStartDateChange,
  onYardDaysChange,
  trainingStartDate,
  trainingMode,
  yardDays
}: UseTrainingDateStepArgs) {
  const { data: locationHours } = useLocationHours(locationExternalId ?? null)
  const storedProgramYardOnly = useEnrollmentStore(state => state.formData.programYardOnly)
  const storedProgramClassOnly = useEnrollmentStore(state => state.formData.programClassOnly)
  const sponsorCompanyId = useEnrollmentStore(state => state.resumedEnrollment?.sponsorCompanyId)

  const programMode = getTrainingProgramMode({
    storedProgramYardOnly,
    storedProgramClassOnly
  })
  const isYardOnly = programMode === 'yard-only'
  const isClassroomOnly = programMode === 'classroom-only'
  const showsClassroomSchedule = programMode !== 'yard-only'
  const showsYardSchedule = programMode !== 'classroom-only'
  const requiresYardDaySelection = showsYardSchedule
  const hasRequiredYardDays = yardDays.length >= 2
  const {
    data: startDateAvailability,
    isLoading: isStartDateAvailabilityLoading,
    isError: isStartDateAvailabilityError
  } = useStartDateAvailability(showsClassroomSchedule, sponsorCompanyId)

  const allowedDays = useMemo(
    () => getAllowedTrainingDays(programMode, yardDays),
    [programMode, yardDays]
  )
  const calendarMinDate = showsClassroomSchedule
    ? startDateAvailability?.minimumStartDate
    : undefined
  const unavailableStartDate = showsClassroomSchedule
    ? startDateAvailability?.unavailableStartDate
    : null
  const availableStartDates = useMemo(
    () =>
      showsClassroomSchedule
        ? (startDateAvailability?.availableStartDates ?? []).map(option => option.date)
        : [],
    [showsClassroomSchedule, startDateAvailability?.availableStartDates]
  )
  const calendarMaxDate = availableStartDates[availableStartDates.length - 1]
  const isStartDateAvailabilityUnavailable =
    showsClassroomSchedule && (isStartDateAvailabilityLoading || isStartDateAvailabilityError)

  useEffect(() => {
    if (!trainingStartDate || isTrainingStartDateDisabled) return
    if (
      !isTrainingStartDateAllowed(trainingStartDate, {
        allowedDays,
        availableStartDates,
        minimumStartDate: calendarMinDate,
        unavailableStartDate
      })
    ) {
      onTrainingStartDateChange('')
    }
  }, [
    allowedDays,
    availableStartDates,
    calendarMinDate,
    isTrainingStartDateDisabled,
    onTrainingStartDateChange,
    trainingStartDate,
    unavailableStartDate
  ])

  const classroomHours = locationHours
    ? formatHourRange(locationHours.branchOpeningHour, locationHours.branchClosingHour)
    : DEFAULT_TRAINING_HOURS
  const classroomSchedule = getClassroomScheduleDisplay(trainingMode, classroomHours)
  const yardTrainingHours = locationHours
    ? formatHourRange(locationHours.yardOpeningHour, locationHours.yardClosingHour)
    : DEFAULT_TRAINING_HOURS

  const scheduleNotice =
    availableStartDates.length > 0
      ? 'Choose one of the available cohort start dates before continuing.'
      : isYardOnly
        ? 'Choose a start date that matches one of your selected yard training days.'
        : 'Classroom training starts on Monday. Choose a Monday start date before continuing.'

  const handleToggleYardDay = (day: string) => {
    onYardDaysChange(yardDays.includes(day) ? yardDays.filter(d => d !== day) : [...yardDays, day])
  }

  const isContinueDisabled =
    !trainingStartDate ||
    (requiresYardDaySelection && !hasRequiredYardDays) ||
    isStartDateAvailabilityUnavailable

  return {
    allowedDays,
    availableStartDates,
    calendarMaxDate,
    calendarMinDate,
    classroomSchedule,
    handleToggleYardDay,
    hasRequiredYardDays,
    isClassroomOnly,
    isContinueDisabled,
    isYardOnly,
    isStartDateAvailabilityError,
    isStartDateAvailabilityUnavailable,
    scheduleNotice,
    showsClassroomSchedule,
    showsYardSchedule,
    yardTrainingHours,
    unavailableStartDate
  }
}
